import { MediaSubtype } from '@accedo/one-plugin-sdk';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { Item, MediaType, MenuItem, ResourceType } from '#/client/models';
import {
  getDefaultMenuItem,
  getNavigationTarget,
} from '#/components/Header/Menu.utils';
import { useConfig } from '#/hooks/useConfig';
import { PageAliasesConfig } from '#/service/config/common.types';
import { falsyToNull, snakeToCamel } from '#/service/utils';
import { tb, useTranslationStore } from '#/store/translation/translation.store';

export interface SeoInfo {
  title?: string;
  localizedTitle?: string;
  item?: Item;
  serviceTitle?: string;
}

interface TranslationContext {
  serviceTitle: string | undefined;
  item?: Item;
  collection?: Item;
  page: {
    localizedTitle: string | undefined;
  };
}

const defaultTranslationKeyMap = {
  [MediaType.Item]: 'web.seo.title.item.default',
  [MediaType.Collection]: 'web.seo.title.collection.default',
  page: 'web.seo.title.page.default',
  default: 'web.seo.title.default',
};

// CORE currently uses following title as fallback therefore we leverage it here as well.
const fallbackTitle = 'Accedo One - Web';

/**
 * Sets the page title based on the current route and given params object.
 * When the given params contain empty properties then we will try to
 * extract meaningful information from the route and the menu's configuration
 *
 */
export const useSeoInfo = ({ title, localizedTitle, item }: SeoInfo) => {
  const location = useLocation();
  const {
    menu: { mainMenuItems },
    featureConfig,
    pages,
  } = useConfig();
  const { serviceTitle } = featureConfig.seo ?? {};
  const { translations } = useTranslationStore();

  useEffect(() => {
    // On hard refresh or reloads the translations might not be loaded yet. therefore we need to wait for them.
    if (Object.keys(translations).length === 0) {
      return;
    }

    document.title =
      (getTitle(
        mainMenuItems,
        location.pathname,
        title,
        localizedTitle,
        item,
        serviceTitle,
        pages
      ) ||
        falsyToNull(serviceTitle)) ??
      fallbackTitle;
  }, [
    translations,
    title,
    localizedTitle,
    item,
    serviceTitle,
    location.pathname,
    mainMenuItems,
    pages,
  ]);
};

const getTitle = (
  mainMenuItems: MenuItem[],
  pathname: string,
  title: SeoInfo['title'],
  localizedTitle: SeoInfo['localizedTitle'],
  item: SeoInfo['item'],
  serviceTitle: SeoInfo['serviceTitle'],
  pages: PageAliasesConfig
) => {
  const context: TranslationContext = {
    serviceTitle,
    item,
    page: {
      localizedTitle: getLocalizedTitle(
        mainMenuItems,
        pathname,
        title,
        localizedTitle,
        pages
      ),
    },
  };

  const translationKey = getTranslationKey(context);

  const itemType =
    item?.type === MediaType.Item ? ResourceType.Item : ResourceType.Collection;

  context.serviceTitle =
    falsyToNull(context.serviceTitle) ?? context.page.localizedTitle;
  context[itemType] = item;

  return tb(translationKey, {
    ...context,
    // disable escaping to prevent apostrophes in the title turning into &amp;
    interpolation: { escapeValue: false },
  });
};

/**
 * Get the localized title of the current page.
 * @param menu Menu from the configuration
 * @param pathname Current pathname from the `location`
 * @param title Custom title from internal pages
 * @param localizedTitle Current page title from the desktop layout template
 * @param pages Page alias configuration
 * @returns title of the currently active page
 */
const getLocalizedTitle = (
  menu: MenuItem[],
  pathname: string,
  title: SeoInfo['title'],
  localizedTitle: SeoInfo['localizedTitle'],
  pages: PageAliasesConfig
) => {
  // Ignore localized title if the user enters home page - should display service title only.
  if (pathname === '/') {
    return undefined;
  }

  // Use custom page title if it exits.
  if (title) {
    return title;
  }

  // Use localized title if it exists, otherwise fallback to the menu item title.
  return localizedTitle ?? getCurrentMenuTitle(menu, pathname, true, pages);
};

const getTranslationKey = (context: TranslationContext) => {
  const { item, page, serviceTitle } = context;

  if (item?.subtype && item?.subtype !== MediaSubtype.None && serviceTitle) {
    // If we have an item, we use the constructed item or collection title.
    return `web.seo.title.${item.type.toLowerCase()}.${snakeToCamel(
      item.subtype
    )}`;
  }

  if (item && (item.subtype === MediaSubtype.None || !item.subtype)) {
    return defaultTranslationKeyMap[item.type];
  }

  // If we have a localized title or custom title with a service title, we use the default page title.
  if (page.localizedTitle && serviceTitle) {
    return defaultTranslationKeyMap['page'];
  }

  // If we have nothing, we use the default title if a service title exists.
  return defaultTranslationKeyMap['default'];
};

/**
 * Gets title of the currently active page from the menu, so it's available for SEO purposes
 * @param menu Menu from the configuration
 * @param pathname Current pathname from the `location`
 * @param ignoreHome @default true - whether to ignore home page
 * @param pages Page alias configuration
 * @returns title of the currently active page
 */
function getCurrentMenuTitle(
  menu: MenuItem[],
  pathname: string,
  ignoreHome: boolean,
  pages: PageAliasesConfig
) {
  if (!menu || !pathname) {
    return undefined;
  }

  const home = getDefaultMenuItem(menu);

  const currentPath =
    !ignoreHome && menu.length > 0 && pathname === '/'
      ? (getNavigationTarget(home, home.id, pages).internalUrl ?? '')
      : pathname;

  const currentMenuItem = menu.find((item) => {
    const itemPath =
      getNavigationTarget(item, home.id, pages).internalUrl ?? '';

    return itemPath.toLowerCase() === currentPath.toLowerCase();
  });

  return currentMenuItem?.display.title;
}
